import { Card } from "@/components/ui/card";
import { ChartBar } from "lucide-react";
import { EmptyState } from "@/components/ui/EmptyState";
import { useSelectedModel } from "@/hooks/useSelectedModel";

const pct = (v: number) => `${(v * 100).toFixed(1)}%`;

export const ModelPerformance = () => {
  const { model } = useSelectedModel();

  const metrics = (model?.metrics ?? {}) as Record<string, number>;
  const type = model?.model_type;

  const tiles =
    type === "regression"
      ? [
          { label: "Variance explained (R²)", value: pct(metrics.r2 ?? 0) },
          { label: "Typical error (RMSE)", value: (metrics.rmse ?? 0).toLocaleString() },
          { label: "Average error (MAE)", value: (metrics.mae ?? 0).toLocaleString() },
          { label: "Rows tested", value: String(metrics.test_rows ?? 0) },
        ]
      : type === "classification"
        ? [
            { label: "Accuracy", value: pct(metrics.accuracy ?? 0) },
            { label: "Precision", value: pct(metrics.precision ?? 0) },
            { label: "Recall", value: pct(metrics.recall ?? 0) },
            { label: "F1 score", value: pct(metrics.f1 ?? 0) },
          ]
        : [
            { label: "Groups found", value: String(metrics.clusters ?? 0) },
            { label: "Largest group", value: String(metrics.largest_group ?? 0) },
            { label: "Spread (inertia)", value: (metrics.inertia ?? 0).toLocaleString() },
            { label: "Rows grouped", value: String(metrics.train_rows ?? 0) },
          ];

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-6">
        <ChartBar className="h-5 w-5" />
        <h2 className="text-xl font-semibold">How well it performs</h2>
      </div>

      {!model ? (
        <EmptyState
          icon={ChartBar}
          title="No trained model yet"
          description="Train a model above and its real measured scores will appear here."
        />
      ) : (
        <>
          <p className="text-sm text-muted-foreground mb-4">
            {model.name} · measured on {metrics.test_rows ?? 0} held-out rows from{" "}
            {model.data_source}
          </p>
          <div className="grid grid-cols-2 gap-4">
            {tiles.map((t) => (
              <div key={t.label} className="p-4 rounded-lg bg-muted/60">
                <div className="text-sm text-muted-foreground font-medium">{t.label}</div>
                <div className="text-2xl font-bold">{t.value}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
};
